import { useLayoutContext } from '@/container/context/layout/layout-context';
import { useauthMeQuery } from '@/types/generated/graphql';
import { cacheOnly } from '@shared/constants';
import { useScreenSize } from '@shared/hooks/use-screen';
import { useUserGuideStore } from '@shared/store/user-guide.store';
import { capitalize } from '@shared/utils/capitalize';
import { Button, Tour, TourProps } from 'antd';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisableTourGuide } from './hooks/use-disable-tour-guide';
import { userGuideTourSteps } from './user-guide-tour.constants';
import { isUserGuideKeyAvailable } from './utils/user-guide-availability';

const TARGET_POLL_INTERVAL_MS = 100;
const TARGET_WAIT_TIMEOUT_MS = 4000;

function resolveTourTarget(tourId: string): HTMLElement | undefined {
  const candidates = document.querySelectorAll(`[data-tour-id="${tourId}"]`);
  for (const el of Array.from(candidates)) {
    const target = (el.closest('.ant-menu-item') as HTMLElement) ?? (el as HTMLElement);
    const rect = target.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return target;
  }
  return undefined;
}

function isSubmenuHighlightReady(route?: string): boolean {
  if (!route?.startsWith('/settings')) return true;
  return Boolean(document.querySelector('.sidebar-submenu .ant-menu-item-selected'));
}

export function UserGuideTourView() {
  const { isTourOpen, tourStartStep, closeTour, openCloseConfirm, setTourProfileDropdownOpen, setTourCurrentIndex } =
    useUserGuideStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSmallerDevice } = useScreenSize();
  const { openSidebarMobileDrawer, closeSidebarMobileDrawer } = useLayoutContext();
  const { disableTourGuide } = useDisableTourGuide();
  const { data } = useauthMeQuery(cacheOnly);
  const tourGuideEnabled = Boolean(data?.authMe?.data?.user?.tourGuideEnabled);
  const tourSteps = userGuideTourSteps(t, tourGuideEnabled).filter((step) => isUserGuideKeyAvailable(step.key));
  const [current, setCurrent] = useState(tourStartStep);
  const [targetReady, setTargetReady] = useState(false);
  const isInitialStep = useRef(true);

  useEffect(() => {
    if (isTourOpen) {
      setCurrent(tourStartStep);
      isInitialStep.current = true;
    }
  }, [isTourOpen, tourStartStep]);

  useEffect(() => {
    setTourCurrentIndex(current);
  }, [current, setTourCurrentIndex]);

  useEffect(() => {
    if (!isTourOpen) return;
    const step = tourSteps[current];
    if (!step) return;

    setTargetReady(false);
    if (step.route && !location.pathname.startsWith(step.route.split('?')[0])) {
      navigate(step.route);
    }
    if (isSmallerDevice) {
      if (step.route) {
        closeSidebarMobileDrawer();
      } else if (!step.openProfileDropdown) {
        openSidebarMobileDrawer();
      }
    }
    setTourProfileDropdownOpen(Boolean(step.openProfileDropdown));

    let cancelled = false;
    let interval: ReturnType<typeof setInterval>;
    const startPoll = () => {
      const startedAt = Date.now();
      interval = setInterval(() => {
        const found = resolveTourTarget(step.tourId) && isSubmenuHighlightReady(step.route);
        const timedOut = Date.now() - startedAt > TARGET_WAIT_TIMEOUT_MS;
        if (!found && !timedOut) return;

        clearInterval(interval);
        if (cancelled) return;
        if (found) {
          setTargetReady(true);
        } else if (current < tourSteps.length - 1) {
          setCurrent(current + 1);
        } else {
          closeTour();
        }
      }, TARGET_POLL_INTERVAL_MS);
    };

    const mobileDrawerDelay = !step.route && isSmallerDevice && isInitialStep.current ? 1500 : 0;
    isInitialStep.current = false;
    const timeout = setTimeout(startPoll, mobileDrawerDelay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [current, isTourOpen]);

  const steps: TourProps['steps'] = tourSteps.map((step) => ({
    title: <span className="text-text-900">{step.title}</span>,
    description: <span className="text-text-600">{step.description}</span>,
    target: () => resolveTourTarget(step.tourId) as HTMLElement,
    ...(step.key === 'user_guide' ? { nextButtonProps: { children: capitalize(t('lets_start')) } } : {}),
  }));

  return (
    <Tour
      rootClassName="user-guide-tour"
      zIndex={1050}
      closable={false}
      placement={isSmallerDevice ? 'bottomLeft' : 'left'}
      open={isTourOpen && targetReady}
      current={current}
      onChange={setCurrent}
      onClose={closeTour}
      onFinish={() => {
        closeTour();
        if (tourSteps[current]?.key === 'user_guide') {
          disableTourGuide();
        } else {
          openCloseConfirm();
        }
      }}
      steps={steps}
      indicatorsRender={() => null}
      actionsRender={(originNode, { current, total }) => {
        const [, nextButton] = (originNode as ReactElement<{ children: [ReactElement, ReactElement] }>).props.children;
        const isLastStep = current === total - 1;
        return (
          <>
            <span>
              {tourGuideEnabled && !isLastStep && (
                <Button size="small" onClick={() => setCurrent(total - 1)}>
                  {capitalize(t('skip_to_last'))}
                </Button>
              )}
            </span>
            <span className="flex items-center gap-2">
              <span className="text-xs text-text-600">
                {current + 1}/{total}
              </span>
              {nextButton}
            </span>
          </>
        );
      }}
    />
  );
}
